import React, { useEffect, useMemo, useState } from "react";
import { Link2 } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import FieldLabel from "../ui/FieldLabel";
import Separator from "../ui/Separator";
import Tooltip from "../ui/Tooltip";
import { useAssetEntries } from "../../stores/assetStore";
import { logger } from "../../utils/logger";
import {
  useBodies,
  useJoints,
  useMeshes,
  useSceneActions,
  useSites,
  useWorldBodies,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { LIBRARY_ATTACHMENTS } from "../../utils/libraryCatalog";
import {
  isAttachmentSiteName,
  pickShallowAttachmentSiteUuidForWorldRoot,
} from "../../utils/libraryGripperAttachTargets";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

/** Match loose grippers / tools in MJCF naming (many grippers use a generic root name like `base`). */
const GRIPPER_NAME_HINT =
  /(gripper|robotiq|2f85|tool|hand|dex|finger|eef|coupling|piper|shadow|allegro|franka|palm|knuckle)/i;

const isGripperLikeLabel = (rootBodyName, worldBodyLabel) => {
  const a = String(rootBodyName || "");
  const b = String(worldBodyLabel || "");
  return GRIPPER_NAME_HINT.test(a) || GRIPPER_NAME_HINT.test(b);
};

/** World object treated as an arm / robot (attach pairings are robot ↔ gripper only). */
const isRobotLikeWorldBody = (worldBodyMapKey, bodies, worldBodyName) => {
  const rootName = bodies?.[worldBodyMapKey]?.name || "";
  return !isGripperLikeLabel(rootName, worldBodyName);
};

const bodyHostsAttachmentSite = (bodyMapKey, bodies, sites) => {
  const body = bodies?.[bodyMapKey];
  if (!body?.sites?.length || !sites) return false;
  for (const ref of body.sites) {
    const su = typeof ref === "string" ? ref : ref?.uuid;
    if (isAttachmentSiteName(sites[su]?.name)) return true;
  }
  return false;
};

/** Walk `bodies` keys and record parent key for each child (first parent wins). */
const buildBodyParentMap = (bodies) => {
  const parentOf = new Map();
  if (!bodies) return parentOf;
  for (const [parentKey, body] of Object.entries(bodies)) {
    if (!body?.children?.length) continue;
    for (const ch of body.children) {
      if (ch && !parentOf.has(ch)) parentOf.set(ch, parentKey);
    }
  }
  return parentOf;
};

const depthFromWorldRoot = (bodyKey, worldRootKey, parentOf) => {
  let d = 0;
  let cur = bodyKey;
  const guard = new Set();
  while (cur && cur !== worldRootKey && !guard.has(cur)) {
    guard.add(cur);
    cur = parentOf.get(cur);
    if (cur) d += 1;
  }
  return cur === worldRootKey ? d : 9999;
};

const markSubtree = (rootKey, bodies, blocked) => {
  const stack = [rootKey];
  const seen = new Set();
  while (stack.length > 0) {
    const k = stack.pop();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    blocked.add(k);
    const b = bodies[k];
    (b?.children || []).forEach((c) => {
      if (c) stack.push(c);
    });
  }
};

/**
 * After attach, the gripper root is parented under an arm link that carries `attachment_site`.
 * List direct children of those hosts (excluding the host that *is* the world root, so standalone
 * grippers do not list their own internal links as “attached”).
 */
const findMountAttachedSubtreeRoots = (worldRootKey, bodies, sites) => {
  if (!worldRootKey || !bodies?.[worldRootKey] || !sites) return [];

  const siteHosts = [];
  const visited = new Set();
  const stack = [worldRootKey];
  while (stack.length > 0) {
    const key = stack.pop();
    if (!key || visited.has(key)) continue;
    visited.add(key);
    const body = bodies[key];
    if (!body) continue;
    if (bodyHostsAttachmentSite(key, bodies, sites)) siteHosts.push(key);
    (body.children || []).forEach((c) => {
      if (c && !visited.has(c)) stack.push(c);
    });
  }

  const parentOf = buildBodyParentMap(bodies);
  siteHosts.sort(
    (a, b) =>
      depthFromWorldRoot(a, worldRootKey, parentOf) - depthFromWorldRoot(b, worldRootKey, parentOf)
  );

  const blocked = new Set();
  const out = [];
  for (const hostKey of siteHosts) {
    if (blocked.has(hostKey)) continue;
    if (hostKey === worldRootKey) continue;

    const host = bodies[hostKey];
    if (!host?.children?.length) continue;

    for (const childId of host.children) {
      if (!childId || blocked.has(childId)) continue;
      const child = bodies[childId];
      if (!child) continue;
      out.push({
        uuid: childId,
        name: child.name || childId,
        mountedOnBodyName: host.name || hostKey,
      });
      markSubtree(childId, bodies, blocked);
    }
  }
  return out;
};

/**
 * Fallback when `findMountAttachedSubtreeRoots` finds nothing: only **direct** children of the
 * world root that look like a gripper. A full subtree scan would list every Shadow link named
 * `*palm*`, `*knuckle*`, etc. as a separate “attached tool”.
 */
const findGripperLikeDirectChildrenOfWorldRoot = (rootWorldBodyKey, bodies) => {
  if (!rootWorldBodyKey || !bodies?.[rootWorldBodyKey]) return [];
  const results = [];
  for (const uuid of bodies[rootWorldBodyKey].children || []) {
    if (!uuid) continue;
    const body = bodies[uuid];
    if (!body) continue;
    if (isGripperLikeLabel(body.name, null)) {
      results.push({ uuid, name: body.name || uuid, mountedOnBodyName: null });
    }
  }
  return results;
};

/**
 * Lowercased text from an attached gripper subtree for catalog matching.
 * Includes joint/site names, mesh URLs, and asset-store paths (`libraryPathPrefix` from fingerprints).
 */
const collectGripperSubtreeHaystack = (rootKey, bodies, meshes, sites, joints, assetEntries) => {
  if (!rootKey || !bodies?.[rootKey]) return "";
  const parts = [];
  const visited = new Set();
  const stack = [rootKey];
  const jointMap = joints && typeof joints === "object" ? joints : {};
  const siteMap = sites && typeof sites === "object" ? sites : {};
  const assets = assetEntries && typeof assetEntries === "object" ? assetEntries : {};

  while (stack.length > 0) {
    const k = stack.pop();
    if (!k || visited.has(k)) continue;
    visited.add(k);
    const b = bodies[k];
    if (!b) continue;
    parts.push(String(b.name || ""));

    for (const jref of Array.isArray(b.joints) ? b.joints : []) {
      const ju = typeof jref === "string" ? jref : jref?.uuid;
      if (!ju) continue;
      const j = jointMap[ju];
      parts.push(String(j?.name || ""));
    }

    for (const sref of Array.isArray(b.sites) ? b.sites : []) {
      const su = typeof sref === "string" ? sref : sref?.uuid;
      if (!su) continue;
      const siteNode = siteMap[su];
      parts.push(String(siteNode?.name || ""));
    }

    const geoms = Array.isArray(b.geoms) ? b.geoms : [];
    for (const g of geoms) {
      if (!g) continue;
      parts.push(String(g.name || ""));
      const meshId = g.mesh;
      if (meshId && meshes?.[meshId]) {
        const m = meshes[meshId];
        parts.push(String(m.name || ""));
        parts.push(String(m.fingerprint || ""));
        parts.push(String(m.url || ""));
        const fp = m.fingerprint;
        if (fp && assets[fp]) {
          const e = assets[fp];
          parts.push(String(e.name || ""));
          const lp = e.sources?.libraryPathPrefix;
          if (lp) parts.push(String(lp));
        }
      }
    }
    for (const ch of b.children || []) {
      const ck = typeof ch === "string" ? ch : ch?.uuid;
      if (ck) stack.push(ck);
    }
  }
  return parts.join(" ").toLowerCase();
};

/** Primary = library object name (e.g. Shadow Hand); subtitle = mount link on the arm. */
const AttachedGripperRowCopy = ({ libraryTitle, bodyName, mountLinkName }) => {
  const primaryLabel = (libraryTitle && String(libraryTitle).trim()) || bodyName || "";
  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-medium leading-snug text-gray-100">{primaryLabel}</div>
      {mountLinkName ? (
        <FieldLabel
          as="span"
          className="mt-0.5 block text-[11px] leading-snug text-gray-400 normal-case"
        >
          mounted to {mountLinkName}
        </FieldLabel>
      ) : null}
    </div>
  );
};

/** Library display name (e.g. "Robotiq 2F-85") — worldBodies no longer lists the gripper after attach. */
const inferGripperLibraryTitle = (
  gripperSubtreeRootKey,
  bodies,
  meshes,
  sites,
  joints,
  assetEntries
) => {
  const hay = collectGripperSubtreeHaystack(
    gripperSubtreeRootKey,
    bodies,
    meshes,
    sites,
    joints,
    assetEntries
  );
  if (!hay) return null;
  const grippers = Array.isArray(LIBRARY_ATTACHMENTS) ? [...LIBRARY_ATTACHMENTS] : [];
  grippers.sort((a, b) => String(b.id || "").length - String(a.id || "").length);
  for (const asset of grippers) {
    const id = String(asset.id || "").toLowerCase();
    if (!id) continue;
    const idFlat = id.replace(/_/g, "");
    if (hay.includes(id) || hay.includes(idFlat)) return asset.name;
  }
  if (hay.includes("robotiq") || hay.includes("2f85")) {
    const hit = grippers.find((g) => g.id === "robotiq_2f85");
    return hit?.name || null;
  }
  if (hay.includes("shadow") || hay.includes("rh_forearm") || hay.includes("rh_wrj")) {
    const hit = grippers.find((g) => g.id === "shadow_hand");
    return hit?.name || null;
  }
  return null;
};

const pickSiteUuid = (sites, preferredUuid) => {
  if (!Array.isArray(sites) || sites.length === 0) return null;
  if (preferredUuid && sites.some((s) => s.siteUuid === preferredUuid)) {
    return preferredUuid;
  }
  return sites[0].siteUuid;
};

/**
 * Keep the inspector on the robot object after attach. A scene rebuild in the same input frame
 * can otherwise deliver a stray pointer-up on the canvas and select the gripper root ("base").
 */
const scheduleRestoreRobotObjectSelection = (robotWorldBodyMapKey, lastSelectionSource) => {
  if (!robotWorldBodyMapKey) return;
  const run = () => {
    useSelectionStore.setState({
      selectedBody: robotWorldBodyMapKey,
      selectedMesh: null,
      selectedJoint: null,
      selectedSite: null,
      selectedGeom: null,
      selectedInertial: null,
    });
    if (lastSelectionSource != null && lastSelectionSource !== "") {
      useSelectionStore.getState().setLastSelectionSource(lastSelectionSource);
    }
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
};

/**
 * Shared “attach by attachment_site” UI for robots and grippers.
 * When there are no floating attach targets (e.g. right after a gripper is attached), still lists
 * mounted subtree roots so the section does not disappear without feedback.
 *
 * @param {object} props
 * @param {string | null} props.worldBodyMapKey — Bodies map key for the world root (robot or gripper object).
 * @param {string | null} [props.preferredGripperSiteUuid] — When the host is the gripper, prefer this site uuid (e.g. selected site in the site panel).
 * @param {boolean} [props.physicsLocked] — When true, collapsible sections show physics lock chrome and block interaction.
 */
const GripperAttachmentSection = ({
  worldBodyMapKey,
  preferredGripperSiteUuid = null,
  physicsLocked = false,
}) => {
  const bodies = useBodies();
  const meshes = useMeshes();
  const sites = useSites();
  const joints = useJoints();
  const assetEntries = useAssetEntries();
  const worldBodies = useWorldBodies();
  const sceneActions = useSceneActions();

  const hostWorldLabel = useMemo(() => {
    if (!worldBodyMapKey) return "";
    const entry = (worldBodies || []).find((wb) => {
      const id = typeof wb === "string" ? wb : wb?.uuid;
      return id === worldBodyMapKey;
    });
    if (entry && typeof entry === "object" && entry.name) return String(entry.name);
    return "";
  }, [worldBodyMapKey, worldBodies]);

  const isGripperHost = useMemo(() => {
    if (!worldBodyMapKey || !bodies?.[worldBodyMapKey]) return false;
    const hostRootName = bodies[worldBodyMapKey].name || "";
    return isGripperLikeLabel(hostRootName, hostWorldLabel);
  }, [worldBodyMapKey, bodies, hostWorldLabel]);

  const { attachTargets, mySites, gripperSiteForAttach, robotSiteForAttach } = useMemo(() => {
    const emptyAttach = {
      attachTargets: [],
      mySites: [],
      gripperSiteForAttach: null,
      robotSiteForAttach: null,
    };
    if (!worldBodyMapKey) return emptyAttach;

    let allSites;
    try {
      allSites = sceneActions.findAttachmentSites("attachment_site") || [];
    } catch (e) {
      logger.warn("[GripperAttachmentSection] findAttachmentSites failed:", e);
      return emptyAttach;
    }

    const myAttachmentSites = allSites.filter((s) => s.worldBodyUuid === worldBodyMapKey);
    if (myAttachmentSites.length === 0) return emptyAttach;

    const seen = new Set();
    const others = [];
    for (const site of allSites) {
      if (site.worldBodyUuid === worldBodyMapKey) continue;
      if (seen.has(site.worldBodyUuid)) continue;
      seen.add(site.worldBodyUuid);
      others.push({
        worldBodyUuid: site.worldBodyUuid,
        worldBodyName: site.worldBodyName,
        bodyName: site.bodyName,
        siteUuid: site.siteUuid,
      });
    }

    let targets;
    if (isGripperHost) {
      targets = others.filter((t) =>
        isRobotLikeWorldBody(t.worldBodyUuid, bodies, t.worldBodyName)
      );
    } else {
      targets = others.filter((t) =>
        isGripperLikeLabel(bodies?.[t.worldBodyUuid]?.name, t.worldBodyName)
      );
    }

    const gripperSiteUuid = isGripperHost
      ? pickSiteUuid(myAttachmentSites, preferredGripperSiteUuid)
      : null;
    const robotSiteUuid = !isGripperHost ? pickSiteUuid(myAttachmentSites, null) : null;

    return {
      attachTargets: targets,
      mySites: myAttachmentSites,
      gripperSiteForAttach: gripperSiteUuid,
      robotSiteForAttach: robotSiteUuid,
    };
  }, [worldBodyMapKey, bodies, sceneActions, preferredGripperSiteUuid, isGripperHost]);

  /** MJCF body that carries *this* host's chosen `attachment_site` (pairs with each target's `bodyName`). */
  const hostAttachBodyName = useMemo(() => {
    const siteUuid = isGripperHost ? gripperSiteForAttach : robotSiteForAttach;
    if (!Array.isArray(mySites) || mySites.length === 0) return "";
    if (siteUuid) {
      const hit = mySites.find((s) => s.siteUuid === siteUuid);
      if (hit?.bodyName) return String(hit.bodyName);
    }
    return mySites[0]?.bodyName ? String(mySites[0].bodyName) : "";
  }, [isGripperHost, gripperSiteForAttach, robotSiteForAttach, mySites]);

  const attachedToolRows = useMemo(() => {
    if (!worldBodyMapKey || !bodies || isGripperHost) return [];
    const mountRows = findMountAttachedSubtreeRoots(worldBodyMapKey, bodies, sites);
    if (mountRows.length > 0) return mountRows;
    return findGripperLikeDirectChildrenOfWorldRoot(worldBodyMapKey, bodies);
  }, [worldBodyMapKey, bodies, sites, isGripperHost]);

  const attachedToolRowsWithCatalog = useMemo(() => {
    if (!attachedToolRows.length || !bodies) return [];
    return attachedToolRows.map((row) => ({
      ...row,
      libraryTitle: inferGripperLibraryTitle(row.uuid, bodies, meshes, sites, joints, assetEntries),
    }));
  }, [attachedToolRows, bodies, meshes, sites, joints, assetEntries]);

  /**
   * Robot world roots with `attachment_site` (including this host), for the reattach submenu.
   * Same data shape as floating “attach to robot” targets; current robot stays in the list so it
   * can be highlighted as the active attachment.
   */
  const robotReattachMenuTargets = useMemo(() => {
    if (!worldBodyMapKey || isGripperHost) return [];
    let allSites;
    try {
      allSites = sceneActions.findAttachmentSites("attachment_site") || [];
    } catch {
      return [];
    }
    const worldLabelFor = (mapKey) => {
      const entry = (worldBodies || []).find((wb) => {
        const id = typeof wb === "string" ? wb : wb?.uuid;
        return id === mapKey;
      });
      if (entry && typeof entry === "object" && entry.name) return String(entry.name);
      return "";
    };
    const candidateRoots = new Set(allSites.map((s) => s.worldBodyUuid).filter((id) => id));
    const out = [];
    for (const wb of candidateRoots) {
      const label = worldLabelFor(wb);
      const rootName = bodies?.[wb]?.name || "";
      if (!isRobotLikeWorldBody(wb, bodies, label)) continue;
      const shallowUuid = pickShallowAttachmentSiteUuidForWorldRoot(bodies, allSites, wb, null);
      if (!shallowUuid) continue;
      const hit = allSites.find((s) => s.siteUuid === shallowUuid && s.worldBodyUuid === wb);
      if (!hit) continue;
      out.push({
        worldBodyUuid: wb,
        worldBodyName: hit.worldBodyName || label || rootName || wb,
        bodyName: hit.bodyName,
        siteUuid: shallowUuid,
      });
    }
    out.sort((a, b) => String(a.worldBodyName).localeCompare(String(b.worldBodyName)));
    return out;
  }, [worldBodyMapKey, isGripperHost, bodies, worldBodies, sceneActions]);

  const [reattachMenuGripperUuid, setReattachMenuGripperUuid] = useState(null);

  useEffect(() => {
    setReattachMenuGripperUuid(null);
  }, [worldBodyMapKey]);

  const handleReattach = (gripperRootKey, robotTarget) => {
    if (
      !gripperRootKey ||
      !robotTarget?.worldBodyUuid ||
      !robotTarget?.siteUuid ||
      !worldBodyMapKey
    )
      return;
    if (robotTarget.worldBodyUuid === worldBodyMapKey) {
      setReattachMenuGripperUuid(null);
      return;
    }
    const { lastSelectionSource: preservedSelectionSource } = useSelectionStore.getState();
    const ok = sceneActions.attachBySites(
      gripperRootKey,
      robotTarget.worldBodyUuid,
      "attachment_site",
      undefined,
      robotTarget.siteUuid
    );
    if (ok) {
      scheduleRestoreRobotObjectSelection(worldBodyMapKey, preservedSelectionSource);
      setReattachMenuGripperUuid(null);
    }
  };

  const handleAttach = (target) => {
    if (!worldBodyMapKey || !target?.worldBodyUuid || !target.siteUuid) return;

    const { lastSelectionSource: preservedSelectionSource } = useSelectionStore.getState();
    const robotWorldBodyMapKey = isGripperHost ? target.worldBodyUuid : worldBodyMapKey;

    let ok;
    if (isGripperHost) {
      const gSite = gripperSiteForAttach || pickSiteUuid(mySites, preferredGripperSiteUuid);
      if (!gSite) return;
      ok = sceneActions.attachBySites(
        worldBodyMapKey,
        target.worldBodyUuid,
        "attachment_site",
        gSite,
        target.siteUuid
      );
    } else {
      const rSite = robotSiteForAttach || pickSiteUuid(mySites, null);
      if (!rSite) return;
      ok = sceneActions.attachBySites(
        target.worldBodyUuid,
        worldBodyMapKey,
        "attachment_site",
        target.siteUuid,
        rSite
      );
    }

    if (ok) {
      scheduleRestoreRobotObjectSelection(robotWorldBodyMapKey, preservedSelectionSource);
    }
  };

  if (!worldBodyMapKey) {
    return null;
  }

  const hasAttachUi = mySites.length > 0 && attachTargets.length > 0;
  const hasAttachedList = !isGripperHost && attachedToolRowsWithCatalog.length > 0;

  if (!hasAttachUi && !hasAttachedList) {
    return null;
  }

  const hostDisplayName = hostWorldLabel || bodies?.[worldBodyMapKey]?.name || worldBodyMapKey;

  return (
    <>
      {hasAttachedList && (
        <InspectorPhysicsLockedCollapsibleWrap locked={physicsLocked}>
          <Collapsible defaultOpen>
            <CollapsibleTrigger quiet>
              <span>Attached gripper / tool</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-2 space-y-2">
                <div className="space-y-1.5">
                  {attachedToolRowsWithCatalog.map((row) => {
                    const showReattach = robotReattachMenuTargets.length > 0;
                    const reattachOpen = reattachMenuGripperUuid === row.uuid;
                    const rowShellClass =
                      "rounded border border-gray-700/80 bg-transparent text-gray-200 overflow-hidden";

                    return (
                      <div key={row.uuid} className={rowShellClass}>
                        <div className="flex min-w-0 items-stretch gap-0.5">
                          <div className="min-w-0 flex-1 px-3 py-2">
                            <AttachedGripperRowCopy
                              libraryTitle={row.libraryTitle}
                              bodyName={row.name}
                              mountLinkName={row.mountedOnBodyName}
                            />
                          </div>
                          {showReattach ? (
                            <Tooltip content="Choose robot (current attachment is highlighted)">
                              <button
                                type="button"
                                data-no-panel-drag="true"
                                aria-label="Choose robot to attach to"
                                aria-expanded={reattachOpen}
                                className="menu-item flex w-10 shrink-0 items-center justify-center border-l border-gray-700 bg-gray-800/80 hover:bg-gray-700/60 text-gray-300 transition-colors"
                                onPointerDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setReattachMenuGripperUuid((prev) =>
                                    prev === row.uuid ? null : row.uuid
                                  );
                                }}
                              >
                                <Link2 className="w-4 h-4" aria-hidden />
                              </button>
                            </Tooltip>
                          ) : null}
                        </div>
                        {reattachOpen && showReattach ? (
                          <div className="border-t border-gray-700/80 bg-transparent px-2 py-2 space-y-2">
                            <FieldLabel className="mb-2">
                              Attach{" "}
                              <span className="font-medium text-gray-200">
                                {row.libraryTitle || row.name}
                              </span>{" "}
                              to:
                            </FieldLabel>
                            <div className="space-y-2">
                              {robotReattachMenuTargets.map((t) => {
                                const isCurrentAttachment = t.worldBodyUuid === worldBodyMapKey;
                                const robotArmLink =
                                  isCurrentAttachment && row.mountedOnBodyName
                                    ? row.mountedOnBodyName
                                    : t.bodyName;
                                const pairLine =
                                  robotArmLink && row.name
                                    ? `${robotArmLink} <--> ${row.name}`
                                    : t.bodyName || "";

                                return (
                                  <button
                                    key={t.worldBodyUuid}
                                    type="button"
                                    data-no-panel-drag="true"
                                    aria-current={isCurrentAttachment ? "true" : undefined}
                                    aria-label={
                                      isCurrentAttachment
                                        ? `Current robot: ${t.worldBodyName}`
                                        : `Attach to ${t.worldBodyName}`
                                    }
                                    className={`w-full flex flex-col items-start px-3 py-2 rounded text-sm transition-colors text-left ${
                                      isCurrentAttachment
                                        ? "bg-gray-700 ring-1 ring-inset ring-gray-500/25 hover:bg-gray-600"
                                        : "bg-gray-700 hover:bg-gray-600"
                                    }`}
                                    onPointerDown={(event) => event.stopPropagation()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleReattach(row.uuid, t);
                                    }}
                                  >
                                    <div className="flex w-full min-w-0 items-center gap-2 text-gray-200">
                                      <span
                                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                          isCurrentAttachment ? "bg-green-500/90" : "bg-transparent"
                                        }`}
                                        aria-hidden
                                      />
                                      <span className="min-w-0 flex-1 font-medium truncate">
                                        {t.worldBodyName}
                                        {isCurrentAttachment ? (
                                          <span className="ml-1.5 text-[11px] font-normal text-green-500/90">
                                            (current)
                                          </span>
                                        ) : null}
                                      </span>
                                    </div>
                                    {pairLine ? (
                                      <FieldLabel as="span">{pairLine}</FieldLabel>
                                    ) : null}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </InspectorPhysicsLockedCollapsibleWrap>
      )}
      {hasAttachedList && hasAttachUi ? <Separator className="my-3" /> : null}
      {attachTargets.length > 0 && (
        <InspectorPhysicsLockedCollapsibleWrap locked={physicsLocked}>
          <Collapsible defaultOpen>
            <CollapsibleTrigger quiet>
              <span>{isGripperHost ? "Attach to Robot" : "Attach gripper / tool"}</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-2 space-y-2">
                <FieldLabel className="mb-2">
                  {isGripperHost ? (
                    <>
                      Attach <span className="font-medium text-gray-200">{hostDisplayName}</span>{" "}
                      to:
                    </>
                  ) : (
                    <>
                      Attach to <span className="font-medium text-gray-200">{hostDisplayName}</span>
                      :
                    </>
                  )}
                </FieldLabel>
                {attachTargets.map((target) => (
                  <button
                    key={target.worldBodyUuid}
                    type="button"
                    className="w-full flex flex-col items-start px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleAttach(target);
                    }}
                  >
                    <div className="flex items-center gap-2 text-gray-200">
                      <span className="font-medium">{target.worldBodyName}</span>
                    </div>
                    <FieldLabel as="span">
                      {hostAttachBodyName && target.bodyName
                        ? `${hostAttachBodyName} <--> ${target.bodyName}`
                        : target.bodyName
                          ? `via ${target.bodyName}`
                          : ""}
                    </FieldLabel>
                  </button>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </InspectorPhysicsLockedCollapsibleWrap>
      )}
      <Separator />
    </>
  );
};

export default React.memo(GripperAttachmentSection);
